import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { feeManagementAPI, authAPI, bankAccountAPI } from '../services/api';

const FeeManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [feeRecords, setFeeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'view', or 'edit'
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [students, setStudents] = useState([]);
  
  // Get initial values from URL or use defaults
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('limit')) || 10);
  const [pagination, setPagination] = useState(null);
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [sendingReceipt, setSendingReceipt] = useState(null); // Track which transaction is sending
  const [sendingReminder, setSendingReminder] = useState(null); // Track which installment reminder is sending
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentData, setEditPaymentData] = useState({
    amount: '',
    paymentDate: '',
    paymentMethod: 'cash',
    transactionId: '',
    installmentNumber: null,
    bankAccount: '',
    notes: ''
  });
  const [bankAccounts, setBankAccounts] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    feeId: null,
    userId: '',
    totalFee: '',
    discount: 0,
    paymentType: 'one-time',
    installments: [],
    notes: ''
  });

  // Payment form state
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash',
    transactionId: '',
    installmentNumber: null,
    bankAccount: '',
    notes: ''
  });
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);

  // Edit payment screenshot state
  const [editPaymentScreenshot, setEditPaymentScreenshot] = useState(null);
  
  // Screenshot modal state
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(null);

  // Convert number to words in Indian numbering system
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 > 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 > 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    let number = Math.floor(num);
    let words = '';
    
    if (number >= 10000000) { // Crore
      words += convertLessThanThousand(Math.floor(number / 10000000)) + ' Crore ';
      number %= 10000000;
    }
    
    if (number >= 100000) { // Lakh
      words += convertLessThanThousand(Math.floor(number / 100000)) + ' Lakh ';
      number %= 100000;
    }
    
    if (number >= 1000) { // Thousand
      words += convertLessThanThousand(Math.floor(number / 1000)) + ' Thousand ';
      number %= 1000;
    }
    
    if (number > 0) {
      words += convertLessThanThousand(number);
    }
    
    return words.trim() + ' Rupees Only';
  };

  // Update URL when filters change
  useEffect(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    if (searchTerm) params.search = searchTerm;
    if (sortBy !== 'createdAt') params.sortBy = sortBy;
    if (sortOrder !== 'desc') params.sortOrder = sortOrder;
    if (currentPage !== 1) params.page = currentPage;
    if (itemsPerPage !== 10) params.limit = itemsPerPage;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    setSearchParams(params);
  }, [statusFilter, searchTerm, sortBy, sortOrder, currentPage, itemsPerPage, startDate, endDate]);

  // Only fetch when pagination changes, not on filter changes
  useEffect(() => {
    fetchFeeRecords();
  }, [currentPage, itemsPerPage]);

  // Initial load and fetch students/bank accounts
  useEffect(() => {
    fetchStudents();
    fetchBankAccounts();
  }, []);

  const fetchFeeRecords = async () => {
    try {
      setLoading(true);
      const params = { 
        status: statusFilter,
        search: searchTerm,
        sortBy: sortBy,
        sortOrder: sortOrder,
        page: currentPage,
        limit: itemsPerPage
      };
      
      // Add date filters if present
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const response = await feeManagementAPI.getAllFees(params);
      setFeeRecords(response.data.feeManagements);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching fee records:', error);
      alert('Failed to fetch fee records');
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await authAPI.getAllStudents(1, 1000);
      console.log('Students response:', response.data);
      // Get all students, not just active ones for fee management
      const allStudents = response.data.students || [];
      console.log('All students:', allStudents);
      setStudents(allStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await bankAccountAPI.getAllBankAccounts({ isActive: true });
      setBankAccounts(response.data.bankAccounts || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleOpenModal = (record = null, mode = 'view') => {
    if (record) {
      setModalMode(mode);
      setSelectedRecord(record);
      setFormData({
        feeId: record._id,
        userId: record.user._id,
        totalFee: record.totalFee,
        discount: record.discount,
        paymentType: record.paymentType,
        installments: record.installments,
        notes: record.notes || ''
      });
    } else {
      setModalMode('create');
      setSelectedRecord(null);
      setFormData({
        feeId: null,
        userId: '',
        totalFee: '',
        discount: 0,
        paymentType: 'one-time',
        installments: [],
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRecord(null);
  };

  const handleAddInstallment = () => {
    const newInstallment = {
      amount: '',
      dueDate: '',
      paidAmount: 0,
      status: 'pending'
    };
    setFormData({
      ...formData,
      installments: [...formData.installments, newInstallment]
    });
  };

  const handleRemoveInstallment = (index) => {
    const updated = formData.installments.filter((_, i) => i !== index);
    setFormData({ ...formData, installments: updated });
  };

  const handleInstallmentChange = (index, field, value) => {
    const updated = [...formData.installments];
    updated[index][field] = value;
    
    // Auto-fill subsequent installment due dates when first installment date is set
    if (index === 0 && field === 'dueDate' && value) {
      const firstDate = new Date(value);
      
      // Set second installment due date (1 month later)
      if (updated[1]) {
        const secondDate = new Date(firstDate);
        secondDate.setMonth(secondDate.getMonth() + 1);
        updated[1].dueDate = secondDate.toISOString().split('T')[0];
      }
      
      // Set third installment due date (2 months later)
      if (updated[2]) {
        const thirdDate = new Date(firstDate);
        thirdDate.setMonth(thirdDate.getMonth() + 2);
        updated[2].dueDate = thirdDate.toISOString().split('T')[0];
      }
    }
    
    setFormData({ ...formData, installments: updated });
  };

  const handleSubmitFee = async (e) => {
    e.preventDefault();
    
    try {
      if (formData.paymentType === 'installments' && formData.installments.length === 0) {
        alert('Please add at least one installment');
        return;
      }

      // Validate installment amounts equal the final amount
      if (formData.paymentType === 'installments') {
        const totalFee = parseFloat(formData.totalFee);
        const discount = parseFloat(formData.discount) || 0;
        const finalAmount = totalFee - discount;
        const installmentSum = formData.installments.reduce(
          (sum, inst) => sum + (parseFloat(inst.amount) || 0), 
          0
        );

        if (installmentSum !== finalAmount) {
          const difference = Math.abs(installmentSum - finalAmount);
          const status = installmentSum > finalAmount ? 'exceeds' : 'is less than';
          
          alert(
            `Invalid installment amounts!\n\n` +
            `Total Fee: ₹${totalFee.toLocaleString()}\n` +
            `Discount: ₹${discount.toLocaleString()}\n` +
            `Final Amount: ₹${finalAmount.toLocaleString()}\n\n` +
            `Sum of Installments: ₹${installmentSum.toLocaleString()}\n` +
            `Difference: ₹${difference.toLocaleString()}\n\n` +
            `The sum of installments ${status} the final amount by ₹${difference.toLocaleString()}.\n` +
            `Please adjust the installment amounts so they equal exactly ₹${finalAmount.toLocaleString()}.`
          );
          return;
        }
      }

      // Ensure all numeric values are sent as numbers
      const feePayload = {
        ...formData,
        totalFee: parseFloat(formData.totalFee),
        discount: parseFloat(formData.discount) || 0,
        installments: formData.installments.map(inst => ({
          ...inst,
          amount: parseFloat(inst.amount) || 0,
          paidAmount: parseFloat(inst.paidAmount) || 0
        }))
      };

      await feeManagementAPI.createOrUpdateFee(feePayload);
      const isEdit = modalMode === 'edit' && formData.feeId;
      alert(isEdit ? 'Fee record updated successfully' : 'New fee record created successfully');
      handleCloseModal();
      fetchFeeRecords();
    } catch (error) {
      console.error('Error saving fee:', error);
      alert(error.response?.data?.message || 'Failed to save fee');
    }
  };

  const handleOpenPaymentModal = (record) => {
    setSelectedRecord(record);
    setPaymentData({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      transactionId: '',
      installmentNumber: null,
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    
    // Validate bank account selection
    if (!paymentData.bankAccount) {
      alert('Please select a bank account');
      return;
    }
    
    // Validate payment date is not in the future
    const selectedDate = new Date(paymentData.paymentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      alert('Payment date cannot be in the future. Please select today or an earlier date.');
      return;
    }
    
    try {
      // Create FormData if there's a file, otherwise use regular object
      let paymentPayload;
      
      if (paymentScreenshot) {
        paymentPayload = new FormData();
        paymentPayload.append('amount', parseFloat(paymentData.amount));
        paymentPayload.append('paymentDate', paymentData.paymentDate);
        paymentPayload.append('paymentMethod', paymentData.paymentMethod);
        paymentPayload.append('transactionId', paymentData.transactionId || '');
        paymentPayload.append('installmentNumber', paymentData.installmentNumber || '');
        paymentPayload.append('bankAccount', paymentData.bankAccount || '');
        paymentPayload.append('notes', paymentData.notes || '');
        paymentPayload.append('paymentScreenshot', paymentScreenshot);
      } else {
        paymentPayload = {
          ...paymentData,
          amount: parseFloat(paymentData.amount)
        };
      }
      
      await feeManagementAPI.recordPayment(selectedRecord._id, paymentPayload);
      alert('Payment recorded successfully');
      setShowPaymentModal(false);
      setPaymentScreenshot(null);
      fetchFeeRecords();
    } catch (error) {
      console.error('Error recording payment:', error);
      alert(error.response?.data?.message || 'Failed to record payment');
    }
  };

  const handleDeleteFee = async (feeId) => {
    if (!window.confirm('Are you sure you want to delete this fee record? This will also delete all payment history.')) {
      return;
    }

    try {
      await feeManagementAPI.deleteFee(feeId);
      alert('Fee record deleted successfully');
      fetchFeeRecords();
    } catch (error) {
      console.error('Error deleting fee:', error);
      alert('Failed to delete fee record');
    }
  };

  const handleViewTransactions = async (record) => {
    setSelectedRecord(record);
    setShowTransactionModal(true);
    setLoadingTransactions(true);
    
    try {
      const response = await feeManagementAPI.getPaymentHistory(record._id);
      setTransactions(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      alert('Failed to fetch payment history');
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleSendReceipt = async (paymentId) => {
    if (!window.confirm('Send payment receipt to student email?')) {
      return;
    }

    setSendingReceipt(paymentId);
    
    try {
      const response = await feeManagementAPI.sendReceipt(paymentId);
      alert('Receipt sent successfully!');
      
      // Update the transaction in the list to reflect email sent status
      setTransactions(prevTransactions => 
        prevTransactions.map(t => 
          t._id === paymentId 
            ? { ...t, emailSent: true, emailSentAt: response.data.emailSentAt }
            : t
        )
      );
    } catch (error) {
      console.error('Error sending receipt:', error);
      alert(error.response?.data?.message || 'Failed to send receipt');
    } finally {
      setSendingReceipt(null);
    }
  };

  const handleSendInstallmentReminder = async (installmentNumber) => {
    if (!window.confirm(`Send payment reminder for Installment #${installmentNumber} to student email?`)) {
      return;
    }

    setSendingReminder(installmentNumber);
    
    try {
      await feeManagementAPI.sendInstallmentReminder(selectedRecord._id, installmentNumber);
      alert('Reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert(error.response?.data?.message || 'Failed to send reminder');
    } finally {
      setSendingReminder(null);
    }
  };

  const handleEditPayment = (transaction) => {
    setEditingPayment(transaction);
    setEditPaymentData({
      amount: transaction.amount,
      paymentDate: new Date(transaction.paymentDate).toISOString().split('T')[0],
      paymentMethod: transaction.paymentMethod,
      transactionId: transaction.transactionId || '',
      installmentNumber: transaction.installmentNumber || null,
      bankAccount: transaction.bankAccount?._id || '',
      notes: transaction.notes || ''
    });
    setShowEditPaymentModal(true);
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    
    // Validate bank account selection
    if (!editPaymentData.bankAccount) {
      alert('Please select a bank account');
      return;
    }
    
    // Validate payment date is not in the future
    const selectedDate = new Date(editPaymentData.paymentDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    
    if (selectedDate > today) {
      alert('Payment date cannot be in the future. Please select today or an earlier date.');
      return;
    }
    
    try {
      // Create FormData if there's a new file, otherwise use regular object
      let paymentPayload;
      
      if (editPaymentScreenshot) {
        paymentPayload = new FormData();
        paymentPayload.append('amount', parseFloat(editPaymentData.amount));
        paymentPayload.append('paymentDate', editPaymentData.paymentDate);
        paymentPayload.append('paymentMethod', editPaymentData.paymentMethod);
        paymentPayload.append('transactionId', editPaymentData.transactionId || '');
        paymentPayload.append('installmentNumber', editPaymentData.installmentNumber || '');
        paymentPayload.append('bankAccount', editPaymentData.bankAccount || '');
        paymentPayload.append('notes', editPaymentData.notes || '');
        paymentPayload.append('paymentScreenshot', editPaymentScreenshot);
      } else {
        paymentPayload = {
          ...editPaymentData,
          amount: parseFloat(editPaymentData.amount)
        };
      }
      
      await feeManagementAPI.updatePayment(editingPayment._id, paymentPayload);
      alert('Payment updated successfully');
      setShowEditPaymentModal(false);
      setEditingPayment(null);
      setEditPaymentScreenshot(null);
      // Refresh transactions
      handleViewTransactions(selectedRecord);
    } catch (error) {
      console.error('Error updating payment:', error);
      alert(error.response?.data?.message || 'Failed to update payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }

    try {
      await feeManagementAPI.deletePayment(paymentId);
      alert('Payment deleted successfully');
      // Refresh transactions
      handleViewTransactions(selectedRecord);
      // Refresh fee records to update amounts
      fetchFeeRecords();
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert(error.response?.data?.message || 'Failed to delete payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'paid': '#48bb78',
      'partial': '#fbbf24',
      'not-started': '#a0aec0',
      'overdue': '#f56565'
    };
    return colors[status] || '#a0aec0';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'paid': 'Paid',
      'partial': 'Partial',
      'not-started': 'Not Started',
      'overdue': 'Overdue'
    };
    return labels[status] || status;
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Search handler
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchFeeRecords();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Date filter handlers
  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);
    setCurrentPage(1);
  };

  const handleEndDateChange = (newEndDate) => {
    setEndDate(newEndDate);
    setCurrentPage(1);
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchFeeRecords();
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortBy !== field) {
      return <span style={{ opacity: 0.3, marginLeft: '5px' }}>⇅</span>;
    }
    return (
      <span style={{ marginLeft: '5px' }}>
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <>
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '20px auto', background: '#ffffff', minHeight: 'calc(100vh - 100px)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', color: '#1a202c' }}>
            Fee Management
          </h1>
          <p style={{ margin: 0, color: '#718096' }}>
            Manage student fees and payments
          </p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            onClick={() => navigate('/admin/fee-dashboard')}
            style={{
              padding: '12px 24px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            📊 Dashboard
          </button>
          <button
            onClick={() => navigate('/admin/pending-fees')}
            style={{
              padding: '12px 24px',
              background: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ⏰ Reminders
          </button>
          <button
            onClick={() => handleOpenModal()}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            + Add Fee Record
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ 
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        {/* All Filters in One Row */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Search */}
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            style={{
              width: '200px',
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px'
            }}
          />

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer',
              minWidth: '130px'
            }}
          >
            <option value="all">All Status</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="not-started">Not Started</option>
            <option value="overdue">Overdue</option>
          </select>

          {/* Date Range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '10px', borderLeft: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', color: '#718096' }}>📅</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              max={endDate || new Date().toISOString().split('T')[0]}
              style={{
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                width: '130px'
              }}
              title="Start Date"
            />
            <span style={{ fontSize: '11px', color: '#cbd5e0' }}>—</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              min={startDate}
              max={new Date().toISOString().split('T')[0]}
              style={{
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                width: '130px'
              }}
              title="End Date"
            />
            {(startDate || endDate) && (
              <button
                onClick={handleClearDateFilter}
                style={{
                  padding: '6px 10px',
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#718096'
                }}
                title="Clear dates"
              >
                ✕
              </button>
            )}
          </div>

          {/* Sort Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '10px', borderLeft: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', color: '#718096' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                minWidth: '120px'
              }}
            >
              <option value="createdAt">Created Date</option>
              <option value="userName">Name</option>
              <option value="userEmail">Email</option>
              <option value="totalFee">Total Fee</option>
              <option value="paidAmount">Paid</option>
              <option value="pendingAmount">Pending</option>
              <option value="status">Status</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '8px 10px',
                background: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                minWidth: '40px'
              }}
              title={`Sort: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          {/* Items Per Page */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', paddingLeft: '10px', borderLeft: '1px solid #e2e8f0' }}>
            <span style={{ fontSize: '12px', color: '#718096' }}>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              style={{
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            style={{
              padding: '8px 16px',
              background: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            🔍 Search
          </button>
        </div>
      </div>

      {/* Fee Records Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#718096' }}>
          Loading fee records...
        </div>
      ) : feeRecords.length === 0 ? (
        <div style={{ 
          background: 'white',
          padding: '50px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#718096'
        }}>
          No fee records found. Click "Add Fee Record" to create one.
        </div>
      ) : (
        <div style={{ 
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th 
                  onClick={() => handleSort('userName')}
                  style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Student <SortIcon field="userName" />
                </th>
                <th 
                  onClick={() => handleSort('totalFee')}
                  style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Total Fee <SortIcon field="totalFee" />
                </th>
                <th 
                  onClick={() => handleSort('paidAmount')}
                  style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Paid <SortIcon field="paidAmount" />
                </th>
                <th 
                  onClick={() => handleSort('pendingAmount')}
                  style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Pending <SortIcon field="pendingAmount" />
                </th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', color: '#2d3748' }}>
                  Payment Type
                </th>
                <th 
                  onClick={() => handleSort('status')}
                  style={{ 
                    padding: '15px', 
                    textAlign: 'left', 
                    fontWeight: '600', 
                    color: '#2d3748',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Status <SortIcon field="status" />
                </th>
                <th style={{ padding: '15px', textAlign: 'right', fontWeight: '600', color: '#2d3748' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {feeRecords.map((record) => (
                <tr 
                  key={record._id}
                  style={{ 
                    borderBottom: '1px solid #e2e8f0',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <td style={{ padding: '15px' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#1a202c' }}>
                        {record.user.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#718096' }}>
                        {record.user.email}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '15px', fontWeight: '500' }}>
                    {formatCurrency(record.finalAmount)}
                  </td>
                  <td style={{ padding: '15px', color: '#48bb78', fontWeight: '500' }}>
                    {formatCurrency(record.paidAmount)}
                  </td>
                  <td style={{ padding: '15px', color: '#f56565', fontWeight: '500' }}>
                    {formatCurrency(record.pendingAmount)}
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: record.paymentType === 'one-time' ? '#e0e7ff' : '#fef3c7',
                      color: record.paymentType === 'one-time' ? '#3730a3' : '#92400e',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {record.paymentType === 'one-time' ? 'One-time' : `${record.installments.length} Installments`}
                    </span>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: `${getStatusColor(record.status)}20`,
                      color: getStatusColor(record.status),
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {getStatusLabel(record.status)}
                    </span>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => handleOpenModal(record, 'view')}
                        style={{
                          padding: '6px 12px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleViewTransactions(record)}
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Transactions
                      </button>
                      {record.status !== 'paid' && (
                        <button
                          onClick={() => handleOpenPaymentModal(record)}
                          style={{
                            padding: '6px 12px',
                            background: '#48bb78',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Record Payment
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && feeRecords.length > 0 && pagination && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '20px',
          padding: '20px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {/* Pagination Info */}
          <div style={{ fontSize: '14px', color: '#718096' }}>
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, pagination.total)} of {pagination.total} results
          </div>

          {/* Pagination Controls */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* First Page */}
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              style={{
                padding: '8px 12px',
                background: currentPage === 1 ? '#f7fafc' : 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                color: currentPage === 1 ? '#cbd5e0' : '#2d3748',
                opacity: currentPage === 1 ? 0.5 : 1
              }}
            >
              ⟪ First
            </button>

            {/* Previous Page */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.hasPrev}
              style={{
                padding: '8px 12px',
                background: !pagination.hasPrev ? '#f7fafc' : 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: !pagination.hasPrev ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                color: !pagination.hasPrev ? '#cbd5e0' : '#2d3748',
                opacity: !pagination.hasPrev ? 0.5 : 1
              }}
            >
              ← Prev
            </button>

            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '5px' }}>
              {[...Array(pagination.pages)].map((_, index) => {
                const pageNum = index + 1;
                // Show first 2, last 2, current, and adjacent pages
                const showPage = 
                  pageNum === 1 || 
                  pageNum === 2 || 
                  pageNum === pagination.pages || 
                  pageNum === pagination.pages - 1 ||
                  Math.abs(pageNum - currentPage) <= 1;

                const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 4;
                const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < pagination.pages - 3;

                if (!showPage && !showEllipsisBefore && !showEllipsisAfter) {
                  return null;
                }

                if (showEllipsisBefore || showEllipsisAfter) {
                  return (
                    <span key={`ellipsis-${pageNum}`} style={{ padding: '8px 4px', color: '#cbd5e0' }}>
                      ...
                    </span>
                  );
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    style={{
                      padding: '8px 12px',
                      background: currentPage === pageNum ? '#667eea' : 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: currentPage === pageNum ? '600' : '400',
                      color: currentPage === pageNum ? 'white' : '#2d3748',
                      minWidth: '36px'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Next Page */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasMore}
              style={{
                padding: '8px 12px',
                background: !pagination.hasMore ? '#f7fafc' : 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: !pagination.hasMore ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                color: !pagination.hasMore ? '#cbd5e0' : '#2d3748',
                opacity: !pagination.hasMore ? 0.5 : 1
              }}
            >
              Next →
            </button>

            {/* Last Page */}
            <button
              onClick={() => handlePageChange(pagination.pages)}
              disabled={currentPage === pagination.pages}
              style={{
                padding: '8px 12px',
                background: currentPage === pagination.pages ? '#f7fafc' : 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: currentPage === pagination.pages ? 'not-allowed' : 'pointer',
                fontSize: '13px',
                color: currentPage === pagination.pages ? '#cbd5e0' : '#2d3748',
                opacity: currentPage === pagination.pages ? 0.5 : 1
              }}
            >
              Last ⟫
            </button>
          </div>
        </div>
      )}
    </div>

      {/* Fee Modal */}
      <Modal 
        open={showModal} 
        onClose={handleCloseModal}
        aria-labelledby="fee-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '800px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          minHeight: 0,
          outline: 'none'
        }}>
          {/* Header with Close Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }} id="fee-modal-title">
              {modalMode === 'create' ? 'Add Fee Record' : modalMode === 'edit' ? 'Edit Fee Record' : 'Fee Details'}
            </h2>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {modalMode === 'view' && selectedRecord && (
                <>
                  <button
                    type="button"
                    onClick={() => handleOpenModal(selectedRecord, 'edit')}
                    style={{
                      padding: '8px 16px',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteFee(selectedRecord._id)}
                    style={{
                      padding: '8px 16px',
                      background: '#f56565',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
              <IconButton 
                onClick={handleCloseModal}
                sx={{ 
                  padding: '8px',
                  '&:hover': { backgroundColor: '#f3f4f6' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <form onSubmit={handleSubmitFee}>
            {/* Student Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Student *
              </label>
              <Select
                value={students.find(s => s._id === formData.userId) ? {
                  value: formData.userId,
                  label: `${students.find(s => s._id === formData.userId)?.name} (${students.find(s => s._id === formData.userId)?.email})`
                } : null}
                onChange={(selected) => setFormData({ ...formData, userId: selected?.value || '' })}
                options={students.map(student => ({
                  value: student._id,
                  label: `${student.name} (${student.email})`
                }))}
                placeholder="Search and select a student..."
                isClearable
                isSearchable
                isDisabled={modalMode === 'view'}
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderColor: '#e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '42px'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>

            {/* Fee Amount */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Total Fee (₹) *
                </label>
                <input
                  type="number"
                  value={formData.totalFee}
                  onChange={(e) => setFormData({ ...formData, totalFee: e.target.value })}
                  required
                  min="0"
                  disabled={modalMode === 'view'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Discount (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    min="0"
                    disabled={modalMode === 'view'}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Final Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={(formData.totalFee || 0) - (formData.discount || 0)}
                    disabled
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      background: '#f7fafc',
                      fontWeight: '600',
                      color: '#1a202c'
                    }}
                  />
                </div>
              </div>

              {/* Amount in Words */}
              {((formData.totalFee || 0) - (formData.discount || 0)) > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#4a5568',
                    fontStyle: 'italic'
                  }}>
                    {numberToWords((formData.totalFee || 0) - (formData.discount || 0))}
                  </div>
                </div>
              )}

              {/* Payment Type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Type *
                </label>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="one-time"
                      checked={formData.paymentType === 'one-time'}
                      onChange={(e) => setFormData({ ...formData, paymentType: e.target.value, installments: [] })}
                      disabled={modalMode === 'view'}
                      style={{ marginRight: '8px' }}
                    />
                    One-time Payment
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="installments"
                      checked={formData.paymentType === 'installments'}
                      onChange={(e) => {
                        // When switching to installments, create 3 empty installments if none exist
                        const installments = formData.installments.length > 0 
                          ? formData.installments 
                          : [
                              { amount: '', dueDate: '', paidAmount: 0, status: 'pending' },
                              { amount: '', dueDate: '', paidAmount: 0, status: 'pending' },
                              { amount: '', dueDate: '', paidAmount: 0, status: 'pending' }
                            ];
                        setFormData({ ...formData, paymentType: e.target.value, installments });
                      }}
                      disabled={modalMode === 'view'}
                      style={{ marginRight: '8px' }}
                    />
                    Installments
                  </label>
                </div>
              </div>

              {/* Installments */}
              {formData.paymentType === 'installments' && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <label style={{ fontWeight: '500' }}>Installments</label>
                    {modalMode !== 'view' && (
                      <button
                        type="button"
                        onClick={handleAddInstallment}
                        style={{
                          padding: '8px 16px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        + Add Installment
                      </button>
                    )}
                  </div>

                  {formData.installments.map((inst, index) => (
                    <div 
                      key={index}
                      style={{
                        background: '#f7fafc',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '10px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <strong>Installment {index + 1}</strong>
                        {modalMode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => handleRemoveInstallment(index)}
                            style={{
                              padding: '4px 8px',
                              background: '#f56565',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                            Amount (₹) *
                          </label>
                          <input
                            type="number"
                            value={inst.amount}
                            onChange={(e) => handleInstallmentChange(index, 'amount', e.target.value)}
                            required
                            min="0"
                            disabled={modalMode === 'view'}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px' }}>
                            Due Date *
                          </label>
                          <input
                            type="date"
                            value={inst.dueDate ? inst.dueDate.split('T')[0] : ''}
                            onChange={(e) => handleInstallmentChange(index, 'dueDate', e.target.value)}
                            disabled={modalMode === 'view'}
                            required
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }}
                          />
                        </div>
                      </div>
                      {/* Amount in Words for Installment */}
                      {inst.amount > 0 && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#4a5568',
                            fontStyle: 'italic'
                          }}>
                            {numberToWords(parseFloat(inst.amount) || 0)}
                          </div>
                        </div>
                      )}
                      {modalMode === 'view' && (
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#718096' }}>
                          <div>Paid: {formatCurrency(inst.paidAmount)}</div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '5px' }}>
                            <div>
                              Status: <span style={{ 
                                color: getStatusColor(inst.status),
                                fontWeight: '500'
                              }}>
                                {getStatusLabel(inst.status)}
                              </span>
                            </div>
                            {inst.status === 'pending' && (
                              <button
                                onClick={() => handleSendInstallmentReminder(inst.installmentNumber)}
                                disabled={sendingReminder === inst.installmentNumber}
                                style={{
                                  padding: '6px 12px',
                                  background: sendingReminder === inst.installmentNumber ? '#cbd5e0' : '#f97316',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: sendingReminder === inst.installmentNumber ? 'not-allowed' : 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                {sendingReminder === inst.installmentNumber ? 'Sending...' : '📧 Send Reminder'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Installments Summary */}
                  {formData.installments.length > 0 && (() => {
                    const totalFee = parseFloat(formData.totalFee) || 0;
                    const discount = parseFloat(formData.discount) || 0;
                    const finalAmount = totalFee - discount;
                    const installmentSum = formData.installments.reduce(
                      (sum, inst) => sum + (parseFloat(inst.amount) || 0), 
                      0
                    );
                    const remaining = finalAmount - installmentSum;
                    const isExactMatch = remaining === 0;
                    
                    return (
                      <div style={{ 
                        marginTop: '15px',
                        padding: '15px',
                        background: isExactMatch ? '#f0fdf4' : '#fef3c7',
                        border: `2px solid ${isExactMatch ? '#86efac' : '#fbbf24'}`,
                        borderRadius: '8px'
                      }}>
                        <div style={{ 
                          fontWeight: '600', 
                          marginBottom: '10px',
                          color: isExactMatch ? '#166534' : '#92400e',
                          fontSize: '14px'
                        }}>
                          📊 Installments Summary
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                          <div>
                            <span style={{ color: '#6b7280' }}>Final Amount:</span>
                            <span style={{ fontWeight: '600', marginLeft: '5px' }}>
                              ₹{finalAmount.toLocaleString()}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: '#6b7280' }}>Sum of Installments:</span>
                            <span style={{ fontWeight: '600', marginLeft: '5px' }}>
                              ₹{installmentSum.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div style={{ 
                          marginTop: '10px', 
                          paddingTop: '10px', 
                          borderTop: '1px solid #e5e7eb',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          {isExactMatch ? (
                            <div style={{ color: '#166534' }}>
                              ✅ Perfect! Installments sum equals the final amount.
                            </div>
                          ) : (
                            <div style={{ color: '#92400e' }}>
                              {remaining > 0 ? (
                                <>⚠️ Remaining: ₹{Math.abs(remaining).toLocaleString()} (Need to add more)</>
                              ) : (
                                <>⚠️ Excess: ₹{Math.abs(remaining).toLocaleString()} (Need to reduce)</>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  disabled={modalMode === 'view'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* View Mode: Payment Info */}
              {modalMode === 'view' && selectedRecord && (
                <div style={{
                  background: '#f7fafc',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ marginTop: 0, marginBottom: '10px' }}>Payment Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                    <div>
                      <span style={{ color: '#718096' }}>Paid Amount:</span>
                      <span style={{ fontWeight: '600', marginLeft: '10px', color: '#48bb78' }}>
                        {formatCurrency(selectedRecord.paidAmount)}
                      </span>
                    </div>
                    <div>
                      <span style={{ color: '#718096' }}>Pending Amount:</span>
                      <span style={{ fontWeight: '600', marginLeft: '10px', color: '#f56565' }}>
                        {formatCurrency(selectedRecord.pendingAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
              <button
                type="button"
                onClick={handleCloseModal}
                style={{
                  padding: '10px 20px',
                  background: '#e2e8f0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {modalMode === 'view' ? 'Close' : 'Cancel'}
              </button>
              {modalMode === 'create' || modalMode === 'edit' ? (
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: modalMode === 'edit' ? '#f59e0b' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {modalMode === 'edit' ? 'Update Fee Record' : 'Save Fee Record'}
                </button>
              ) : null}
            </div>
          </form>
        </Box>
      </Modal>

      {/* Payment Modal */}
      <Modal 
        open={showPaymentModal && selectedRecord !== null}
        onClose={() => setShowPaymentModal(false)}
        aria-labelledby="payment-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '90%', md: '600px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          minHeight: 0,
          outline: 'none'
        }}>
          {/* Header with Close Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }} id="payment-modal-title">
              Record Payment
            </h2>
            <IconButton 
              onClick={() => setShowPaymentModal(false)}
              sx={{ 
                padding: '8px',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div style={{
            background: '#f7fafc',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '5px' }}>
              {selectedRecord?.user?.name}
            </div>
            <div style={{ fontSize: '14px', color: '#718096' }}>
              Pending: {formatCurrency(selectedRecord?.pendingAmount)}
            </div>
          </div>

          <form onSubmit={handleRecordPayment}>
            {/* Amount */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Amount (₹) *
              </label>
              <input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                required
                min="0"
                max={selectedRecord?.pendingAmount}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
                {/* Amount in Words */}
                {paymentData.amount > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#4a5568',
                      fontStyle: 'italic'
                    }}>
                      {numberToWords(parseFloat(paymentData.amount) || 0)}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Date */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Payment Method */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Method *
                </label>
                <select
                  value={paymentData.paymentMethod}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="bank-transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Transaction ID */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Transaction ID
                </label>
                <input
                  type="text"
                  value={paymentData.transactionId}
                  onChange={(e) => setPaymentData({ ...paymentData, transactionId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Bank Account */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Bank Account <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={paymentData.bankAccount}
                  onChange={(e) => setPaymentData({ ...paymentData, bankAccount: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select bank account *</option>
                  {bankAccounts.map((account) => (
                    <option key={account._id} value={account._id}>
                      {account.accountName} - {account.bankName} ({account.accountNumber})
                    </option>
                  ))}
                </select>
              </div>

              {/* Installment Number (if applicable) */}
              {selectedRecord?.paymentType === 'installments' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Installment Number
                  </label>
                  <select
                    value={paymentData.installmentNumber || ''}
                    onChange={(e) => setPaymentData({ 
                      ...paymentData, 
                      installmentNumber: e.target.value ? parseInt(e.target.value) : null 
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select installment (optional)</option>
                    {selectedRecord?.installments
                      ?.filter(inst => inst.status !== 'paid')
                      .map((inst, idx) => (
                        <option key={idx} value={inst.installmentNumber}>
                          Installment {inst.installmentNumber} - {formatCurrency(inst.amount)} 
                          {inst.paidAmount > 0 ? ` (Paid: ${formatCurrency(inst.paidAmount)})` : ''}
                          {' '}(Due: {formatDate(inst.dueDate)})
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Payment Screenshot */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Screenshot (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPaymentScreenshot(e.target.files[0])}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                {paymentScreenshot && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#48bb78' }}>
                    Selected: {paymentScreenshot.name}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#2d3748',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#48bb78',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Record Payment
                </button>
              </div>
            </form>
        </Box>
      </Modal>

      {/* Edit Payment Modal */}
      {showEditPaymentModal && editingPayment && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            zIndex: 1051,
            padding: '20px',
            paddingTop: '80px',
            overflowY: 'auto'
          }}
          onClick={() => setShowEditPaymentModal(false)}
        >
          <div 
            style={{
              background: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '30px',
              margin: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              Edit Payment - {editingPayment.receiptNumber}
            </h3>

            <form onSubmit={handleUpdatePayment}>
              {/* Amount */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Amount *
                </label>
                <input
                  type="number"
                  value={editPaymentData.amount}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: e.target.value })}
                  required
                  min="1"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                {/* Amount in Words */}
                {editPaymentData.amount > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#4a5568',
                      fontStyle: 'italic'
                    }}>
                      {numberToWords(parseFloat(editPaymentData.amount) || 0)}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Date */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={editPaymentData.paymentDate}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, paymentDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Payment Method */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Method *
                </label>
                <select
                  value={editPaymentData.paymentMethod}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, paymentMethod: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="upi">UPI</option>
                  <option value="bank-transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Transaction ID */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Transaction ID
                </label>
                <input
                  type="text"
                  value={editPaymentData.transactionId}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, transactionId: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Bank Account */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Bank Account <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={editPaymentData.bankAccount}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, bankAccount: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select bank account *</option>
                  {bankAccounts.map((account) => (
                    <option key={account._id} value={account._id}>
                      {account.accountName} - {account.bankName} ({account.accountNumber})
                    </option>
                  ))}
                </select>
              </div>

              {/* Installment Number (if applicable) */}
              {selectedRecord.paymentType === 'installments' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Installment Number
                  </label>
                  <select
                    value={editPaymentData.installmentNumber || ''}
                    onChange={(e) => setEditPaymentData({ 
                      ...editPaymentData, 
                      installmentNumber: e.target.value ? parseInt(e.target.value) : null 
                    })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select installment (optional)</option>
                    {selectedRecord.installments
                      .filter(inst => inst.status !== 'paid')
                      .map((inst, idx) => (
                        <option key={idx} value={inst.installmentNumber}>
                          Installment {inst.installmentNumber} - {formatCurrency(inst.amount)} 
                          {inst.paidAmount > 0 ? ` (Paid: ${formatCurrency(inst.paidAmount)})` : ''}
                          {' '}(Due: {formatDate(inst.dueDate)})
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}

              {/* Notes */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Notes
                </label>
                <textarea
                  value={editPaymentData.notes}
                  onChange={(e) => setEditPaymentData({ ...editPaymentData, notes: e.target.value })}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Payment Screenshot */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Payment Screenshot (Optional)
                </label>
                {editingPayment?.paymentScreenshot && !editPaymentScreenshot && (
                  <div style={{ marginBottom: '10px', fontSize: '13px', color: '#718096' }}>
                    Current: Screenshot uploaded
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditPaymentScreenshot(e.target.files[0])}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
                {editPaymentScreenshot && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#f59e0b' }}>
                    New file: {editPaymentScreenshot.name} (will replace existing)
                  </div>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditPaymentModal(false);
                    setEditingPayment(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#2d3748',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Update Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      <Modal 
        open={showTransactionModal && selectedRecord !== null}
        onClose={() => setShowTransactionModal(false)}
        aria-labelledby="transaction-modal-title"
        disableScrollLock={true}
        BackdropProps={{ timeout: 0 }}
      >
        <Box sx={{
          position: 'absolute',
          top: '5%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: '95%', sm: '95%', md: '1000px' },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 3,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
          minHeight: 0,
          outline: 'none'
        }}>
          {/* Header with Close Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }} id="transaction-modal-title">
              Payment Transactions - {selectedRecord?.user?.name}
            </h3>
            <IconButton 
              onClick={() => {
                setShowTransactionModal(false);
                setTransactions([]);
              }}
              sx={{ 
                padding: '8px',
                '&:hover': { backgroundColor: '#f3f4f6' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          {/* Student Info Summary */}
          <div style={{
            background: '#f7fafc',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '14px' }}>
              <div>
                <span style={{ color: '#718096', display: 'block', marginBottom: '5px' }}>Total Fee:</span>
                <span style={{ fontWeight: '600', fontSize: '16px' }}>{formatCurrency(selectedRecord?.totalFee)}</span>
              </div>
              <div>
                <span style={{ color: '#718096', display: 'block', marginBottom: '5px' }}>Paid Amount:</span>
                <span style={{ fontWeight: '600', fontSize: '16px', color: '#48bb78' }}>{formatCurrency(selectedRecord?.paidAmount)}</span>
              </div>
              <div>
                <span style={{ color: '#718096', display: 'block', marginBottom: '5px' }}>Pending Amount:</span>
                <span style={{ fontWeight: '600', fontSize: '16px', color: '#f56565' }}>{formatCurrency(selectedRecord?.pendingAmount)}</span>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          {loadingTransactions ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              Loading transactions...
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
              No transactions found
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f7fafc' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Receipt #</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Amount</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Method</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Transaction ID</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Bank Account</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Installment</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Recorded By</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Email Status</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Actions</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', fontSize: '13px', color: '#4a5568' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: '500', color: '#667eea' }}>
                        {transaction.receiptNumber}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {formatDate(transaction.paymentDate)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', fontWeight: '600', textAlign: 'right', color: '#48bb78' }}>
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <span style={{
                          padding: '4px 8px',
                          background: '#e0e7ff',
                          color: '#3730a3',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          textTransform: 'capitalize'
                        }}>
                          {transaction.paymentMethod.replace('-', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#718096' }}>
                        {transaction.transactionId || '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {transaction.bankAccount ? (
                          <div>
                            <div style={{ fontWeight: '500', color: '#2d3748' }}>
                              {transaction.bankAccount.accountName}
                            </div>
                            <div style={{ fontSize: '11px', color: '#718096' }}>
                              {transaction.bankAccount.bankName}
                            </div>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center' }}>
                        {transaction.installmentNumber ? (
                          <span style={{
                            padding: '4px 8px',
                            background: '#fef3c7',
                            color: '#92400e',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            #{transaction.installmentNumber}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {transaction.recordedBy?.name || 'System'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', textAlign: 'center' }}>
                        {transaction.emailSent ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <span style={{
                              padding: '4px 8px',
                              background: '#d4edda',
                              color: '#155724',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              ✓ Sent
                            </span>
                            {transaction.emailSentAt && (
                              <span style={{ fontSize: '10px', color: '#718096' }}>
                                {formatDate(transaction.emailSentAt)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{
                            padding: '4px 8px',
                            background: '#f0f0f0',
                            color: '#666',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            Not Sent
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleSendReceipt(transaction._id)}
                            disabled={sendingReceipt === transaction._id}
                            title={transaction.emailSent ? 'Resend Receipt' : 'Send Receipt'}
                            style={{
                              padding: '6px 10px',
                              background: sendingReceipt === transaction._id ? '#cbd5e0' : '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: sendingReceipt === transaction._id ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {sendingReceipt === transaction._id ? '...' : '📧'}
                          </button>
                          <button
                            onClick={() => handleEditPayment(transaction)}
                            title="Edit Payment"
                            style={{
                              padding: '6px 10px',
                              background: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            ✏️
                          </button>
                          {transaction.paymentScreenshot && (
                            <button
                              onClick={() => {
                                setCurrentScreenshot(transaction.paymentScreenshot);
                                setShowScreenshotModal(true);
                              }}
                              title="View Screenshot"
                              style={{
                                padding: '6px 10px',
                                background: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}
                            >
                              🖼️
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletePayment(transaction._id)}
                            title="Delete Payment"
                            style={{
                              padding: '6px 10px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', color: '#718096', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {transaction.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Total Summary */}
          {transactions.length > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              background: '#f7fafc',
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontWeight: '600', color: '#4a5568' }}>
                Total Transactions: {transactions.length}
              </span>
              <span style={{ fontWeight: '600', fontSize: '16px', color: '#48bb78' }}>
                Total Paid: {formatCurrency(transactions.reduce((sum, t) => sum + t.amount, 0))}
              </span>
            </div>
          )}

          {/* Close Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              onClick={() => {
                setShowTransactionModal(false);
                setTransactions([]);
              }}
              style={{
                padding: '10px 20px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Close
            </button>
          </div>
        </Box>
      </Modal>

      {/* Screenshot Modal */}
      {showScreenshotModal && currentScreenshot && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}
        onClick={() => setShowScreenshotModal(false)}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '20px'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0 }}>Payment Screenshot</h3>
              <button
                onClick={() => setShowScreenshotModal(false)}
                style={{
                  padding: '8px 16px',
                  background: '#e2e8f0',
                  color: '#2d3748',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
            <div style={{ textAlign: 'center' }}>
              <img 
                src={`http://localhost:5000/${currentScreenshot}`} 
                alt="Payment Screenshot" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FeeManagement;
